export enum ProductModule {
  FAMOUS_INTEGRATION = "famous-integration",
  ORDERFUL_EDI = "orderful-edi",
  ORDER_MANAGEMENT = "order-management",
  REBATES = "rebates",
  PROCUREMENT = "procurement",
  INVENTORY_MANAGEMENT = "inventory-management",
  SHIP_AND_DEBIT = "ship-and-debit",
  AP_INVOICING = "ap-invoicing",
  AR_INVOICING = "ar-invoicing",
  ORACLE_EBS_INTEGRATION = "oracle-ebs-integration",
  ORACLE_FUSION_INTEGRATION = "oracle-fusion-integration",
}

// Note: changes to these values can break things in polymorphic relationships. Please be careful.
export enum AIWorkflow {
  EMAIL_SO_INGESTION = "EMAIL_SO_INGESTION",
  PRICE_SHEET_INGESTION = "PRICE_SHEET_INGESTION",
  AP_INVOICE_INGESTION = "AP_INVOICE_INGESTION",
  AR_COLLECTIONS = "AR_COLLECTIONS",
}

/**
 * Org-facing speed↔quality preference for the AI short-pay recommendation.
 * Semantic on purpose: the backend maps each value to a (model, reasoning
 * effort) pair, so the underlying models can change without touching this
 * org-facing contract. Ordered fastest → highest quality.
 */
export const AR_RECOMMENDATION_SPEED_PREFERENCES = [
  "faster",
  "balanced",
  "highest_quality",
] as const;

export type ArRecommendationSpeedPreference =
  (typeof AR_RECOMMENDATION_SPEED_PREFERENCES)[number];

/**
 * Date basis used to age AR invoices on statement / report surfaces. Mirrors
 * `agingBasisSchema` in the rpc contract (which derives its enum from this
 * list) so there is a single source of truth for the basis values.
 */
export const AR_AGING_BASES = [
  "days_overdue",
  "days_since_invoice",
  "days_since_shipment",
] as const;

export type ArAgingBasis = (typeof AR_AGING_BASES)[number];

export const EMAIL_PROVIDER = {
  SENDGRID: "sendgrid",
  MICROSOFT_GRAPH: "microsoft_graph",
} as const;

export type EmailProvider =
  (typeof EMAIL_PROVIDER)[keyof typeof EMAIL_PROVIDER];

export const WorkflowStepMapping = {
  [AIWorkflow.EMAIL_SO_INGESTION]: ["EXTRACT_SALES_ORDER"],
  [AIWorkflow.PRICE_SHEET_INGESTION]: ["PRICE_SHEET_INGESTION"],
  [AIWorkflow.AP_INVOICE_INGESTION]: ["AP_INVOICE_INGESTION"],
  [AIWorkflow.AR_COLLECTIONS]: ["SHORT_PAY_RECOMMENDATION"],
} as const satisfies Record<AIWorkflow, string[]>;

export type WorkflowStep =
  (typeof WorkflowStepMapping)[keyof typeof WorkflowStepMapping][number];

export const WorkflowStepValues = Object.values(WorkflowStepMapping).flat();

/**
 * Sources for payment-term precedence when deriving AP invoice due dates.
 * - "invoice": pay terms extracted from the invoice itself
 * - "supplier": pay terms configured on the supplier record
 * - "company": org-level fallback (default net days)
 */
export type PaymentTermsSource = "invoice" | "supplier" | "company";

export type RegisteredEmailHeaderOption = "to" | "cc";

export type RegisteredEmailDomain = {
  header: RegisteredEmailHeaderOption;
  domain: string;
};

export type AIWorkflowConfig = {
  registeredEmailDomains?: RegisteredEmailDomain[];
};

/**
 * Approval threshold for payment amounts.
 * Used to configure multi-level approval requirements based on payment value.
 *
 * Example thresholds:
 * - { minAmount: 0, requiredApprovals: 1 }      // $0-999 = 1 approval
 * - { minAmount: 1000, requiredApprovals: 2 }   // $1000-9999 = 2 approvals
 * - { minAmount: 10000, requiredApprovals: 3 }  // $10000+ = 3 approvals
 *
 * The highest matching threshold applies (minAmount <= paymentAmount).
 */
export interface ApprovalThreshold {
  /** Payment amounts >= this threshold require this level of approval */
  minAmount: number;
  /** Number of approvals required for this threshold */
  requiredApprovals: number;
}

/**
 * Defaults applied when the org config has no value set for the AR
 * "Send to Internal only" safety toggle. The default is intentionally
 * fail-safe: protection is on, with the Gently corporate domains
 * pre-allow-listed. Customers override by saving explicit values.
 */
export const AR_DEFAULT_INTERNAL_ONLY_ENABLED = true;
export const AR_DEFAULT_INTERNAL_ONLY_DOMAINS: readonly string[] = [
  "gently-ai.com",
  "gentlyhq.com",
];

// Type for module configuration objects
export interface ModuleConfig {
  emailWorkflows?: {
    [key in AIWorkflow]?: AIWorkflowConfig;
  };
  rebates?: {
    reportedAlgorithm?: "linear-regression" | "conservative";
    conservativeRiskFactor?: number;
    seasonalDampeningFactor?: number;
    enabledStrategies?: ("linear-regression" | "conservative")[];
    /**
     * Per-year overrides for forecasting configuration.
     * Keys are 4-digit year strings (e.g. "2025", "2026").
     * Any field set here takes precedence over the top-level default for that year.
     * If a field is not set for a given year, the top-level value is used.
     *
     * FUTURE DIRECTION:
     * We may eventually support per-agreement overrides (e.g. a `forecastConfig`
     * JSONB column on `rebate_agreements`). The resolution order would become:
     *   agreement.forecastConfig  →  perYear[year]  →  org-level default
     * For multi-agreement views (analytics, reports), per-agreement algorithms
     * would require a "blended" prediction — the aggregation service would need
     * to construct a synthetic prediction set by picking each agreement's
     * configured algorithm's data and summing them. The API response shape
     * (`reportedAlgorithm: string`) would likely become either a blended
     * `'selected'` key or a per-agreement map.
     */
    perYear?: Record<
      string,
      {
        reportedAlgorithm?: "linear-regression" | "conservative";
        conservativeRiskFactor?: number;
        seasonalDampeningFactor?: number;
        enabledStrategies?: ("linear-regression" | "conservative")[];
      }
    >;
    /** Mailbox UPN to send vendor followup emails from (e.g. "rebates@company.com") */
    followupMailboxUpn?: string;
    /** Email template ID used for vendor followup emails */
    followupTemplateId?: string;
    /** CoreEnumOption ID for the contact tag used to find vendor followup recipients */
    followupContactTagOptionId?: string;
    /** Default number of days between followup emails per vendor (default: 30) */
    followupCadenceDays?: number;
    /**
     * CoreEnumOption ID (a "Rebate Rule Tags" option) that marks a rule as
     * belonging to the primary "Redistribution" bucket. ID-based so that
     * renaming the tag does not change categorization. When unset, the write
     * path falls back to a legacy `/redist/i` match on tag names.
     */
    primaryBucketingTagId?: string;
  };
  ap?: {
    invoiceLineItemPurchaseMatching?: string[][]; // Array of arrays of SystemEntity for AND/OR logic
    /** When true, suppress sending remittance emails after payment execution */
    disableRemittanceEmail?: boolean;
    /** Mailbox UPN used to send AP remittance emails through Microsoft Graph */
    remittanceMailboxUpn?: string;
    /** Optional visible From address when sending AP remittances as a group or alias */
    remittanceSenderAddress?: string;
    /** Contact email addresses shown in the remittance email body for supplier inquiries */
    remittanceContactEmails?: string[];
    /** Contact phone number shown in the remittance email body for supplier inquiries */
    remittanceContactPhone?: string;
    // TODO: Add SystemEntity.GOODS_RECEIPT_LINE_ITEM for 3-way matching
    paymentScheduling?: {
      mode: "immediate" | "payRun" | "latestDate";
      cron?: string; // for payRun mode
      immediacy?: "asap" | "latest"; // for payRun mode
      rounding?: "up" | "down" | "nearest"; // for payRun mode
      timezone?: string; // for payRun mode
      daysBeforeDue?: number; // for latestDate mode
      /** Ordered precedence for resolving payment terms when no explicit due date is provided */
      paymentTermsPriority?: PaymentTermsSource[];
    };
    /**
     * Default time of day (HH:mm, 24-hour format) when outgoing payments should be executed.
     * Applied to scheduled payment dates in the organization's timezone.
     * Example: "09:00" for 9 AM, "14:30" for 2:30 PM.
     * If not set, payment dates use midnight (start of day).
     */
    defaultOutgoingPaymentTime?: string;
    /**
     * Approval thresholds for payment amounts.
     * If not configured or empty, no approval is required.
     * Authorization (who can approve) is handled by CASL rules (Action.Execute on Subject.APInvoicePayment).
     */
    approvalThresholds?: ApprovalThreshold[];
  };
  ar?: {
    /**
     * Mailbox UPN used to send AR statements and invoices through Microsoft
     * Graph (e.g. "ar@customer-tenant.com"). When unset, AR emails fall back
     * to the default SendGrid verified sender.
     */
    outboxMailboxUpn?: string;
    /**
     * Optional visible From address when the configured mailbox has permission
     * to send as a group or alias. Only honored when `outboxMailboxUpn` is set.
     */
    outboxSenderAddress?: string;
    /**
     * Contact email addresses appended to AR statement and invoice emails so
     * customers know who to reach with questions. Rendered as mailto links.
     */
    outboxContactEmails?: string[];
    /** Contact phone number appended to AR statement and invoice emails. */
    outboxContactPhone?: string;
    /**
     * Logo URL used on AR statement PDFs. When unset, the AR statement
     * generator falls back to the organization-level logo
     * (`OrganizationConfig.logoUrl`).
     */
    logoUrl?: string;
    /**
     * Prefix prepended to the default AR statement PDF attachment filename
     * (e.g. "ACME" → "ACME-statement-CustomerName-2026-05-14.pdf"). Non-
     * alphanumeric characters are stripped during render. Has no effect when
     * the sender supplies a per-send override.
     */
    defaultPdfFilenamePrefix?: string;
    /**
     * Safety gate: when true, AR statement sends are blocked if any
     * To / CC / BCC recipient is outside the configured allow-list of
     * internal email domains. Use to prevent accidental delivery of test
     * data or sensitive aging reports to external recipients. Defaults
     * to AR_DEFAULT_INTERNAL_ONLY_ENABLED when unset on the org config.
     */
    internalOnlyEnabled?: boolean;
    /**
     * Allow-list of email domains treated as internal when
     * `internalOnlyEnabled` is true. Compared case-insensitively against
     * the portion after the @ of each recipient address. Stored as a list;
     * the UI accepts a comma-separated input. Defaults to
     * AR_DEFAULT_INTERNAL_ONLY_DOMAINS when unset on the org config.
     */
    internalOnlyDomains?: string[];
    /**
     * Date basis pre-selected for AR statement / report aging (due date,
     * invoice date, or ship date). When `lockStatementBasis` is true this
     * basis is forced everywhere and the in-UI basis selector is hidden.
     * When unset, surfaces fall back to their own default basis.
     */
    statementDefaultBasis?: ArAgingBasis;
    /**
     * When true, hide the basis selector on AR statement / report surfaces
     * (bulk send dialog, summary aging, custom report builder) and force
     * `statementDefaultBasis`. Stopgap for orgs that only ever use a single
     * basis and find the selector distracting. Requires `statementDefaultBasis`
     * and defaults to off, so surfaces are unchanged until an org opts in.
     */
    lockStatementBasis?: boolean;
    /**
     * Configuration for collections workflows (short-pay auto-detection, etc.).
     */
    collections?: {
      /**
       * Master switch for auto-detection of short pays. Opt-in: when unset or
       * false, the detection service is a no-op for this org.
       */
      autoDetectShortPaysEnabled?: boolean;
      /**
       * Minimum residual balance (decimal string, in the invoice currency) to
       * consider an invoice short-paid. Residuals below this floor are ignored
       * so $0.50 rounding pennies don't generate threads. Default: "10".
       */
      shortPayMinimumAmount?: string;
      /**
       * Number of days to wait after the most recent payment application before
       * opening a short_pay thread. Gives the customer a grace window to make a
       * follow-up payment. Default: 3.
       */
      shortPayGraceDays?: number;
      /**
       * Master switch for auto-detection of over-payments (customer paid more
       * than the invoice balance). Opt-in: no-op when unset or false.
       */
      autoDetectOverPaymentsEnabled?: boolean;
      /**
       * Minimum over-payment (decimal string) to open an over_payment thread.
       * Default: "10".
       */
      overPaymentMinimumAmount?: string;
      /**
       * Master switch for auto-detection of deductions claimed on remittances.
       * Opt-in: no-op when unset or false.
       */
      autoDetectDeductionsEnabled?: boolean;
      /**
       * Minimum deduction amount (decimal string) to open a deduction thread.
       * Default: "10".
       */
      deductionMinimumAmount?: string;
      /**
       * Default user (id of an org user) assigned to newly created collection
       * threads. Applied to auto-detected short-pay threads and pre-selected in
       * the manual create-thread form. When unset, new threads start unassigned.
       */
      defaultAssigneeUserId?: string;
      /**
       * AI short-pay recommendation tuning. Exposed as a semantic speed↔quality
       * preference rather than raw model/effort knobs so the org-facing contract
       * stays stable as the underlying model lineup changes; the backend maps
       * each preference to a (model, reasoning effort) pair. Applies to
       * recommendations generated after the change (cached recs are not
       * retroactively regenerated). Default: "faster".
       */
      recommendations?: {
        speedPreference?: ArRecommendationSpeedPreference;
      };
    };
    /**
     * Configuration for reconciling Famous-ingested cash receipts (bank-side
     * settlement) against remittance-created payments (customer-side advice).
     */
    cashReceiptReconciliation?: {
      /**
       * Window (in days) around a bank transaction's date to search for a
       * matching remittance-created payment. Default: 2.
       */
      dateWindowDays?: number;
    };
  };
  orderManagement?: {
    /**
     * Customer IDs that are explicitly enabled for PO ingestion (allowlist).
     * When populated, ONLY these customers will have sales order drafts created.
     * When empty or undefined, falls back to the denylist / all-enabled default.
     */
    enabledCustomerIds?: string[];
    /**
     * @deprecated Use `enabledCustomerIds` instead. Kept for backward compatibility.
     * Customer IDs that are explicitly disabled from PO ingestion (denylist).
     * Ignored when `enabledCustomerIds` is populated.
     */
    disabledCustomerIds?: string[];
    /**
     * Strategy for computing request/delivery dates on sales order drafts.
     * - "inventory-lead-time": Uses inventory status + product lead times
     * - "shipping-method": Uses shipping method (AIR vs OCEAN) to determine lead time
     */
    requestDateStrategy?: "inventory-lead-time" | "shipping-method";
    /**
     * Whether to allow automated order submission via workflows.
     * When false (default), the submit_sales_orders step will skip execution.
     * When true, orders can be auto-submitted without human intervention.
     */
    autoSubmitEnabled?: boolean;
    /**
     * Whether real Oracle EBS submission is enabled for this organization.
     *
     * When `true`: clicking "Approve and Create Sales Order" POSTs the
     * payload to EBS as normal. This is the fully-live mode.
     *
     * When `false` or omitted (**default, safe**): the Approve button still
     * works and the adapter still builds the exact EBS payload and logs it
     * under `[SALES_ORDER_CREATION_PAYLOAD]`, but the HTTP POST is skipped.
     * The order is marked synced with `origSysDocRef = "DRY_RUN:<ref>"` so
     * dry-run submissions are visually and queryably distinct from real ones.
     *
     * This is a **dry-run by default** kill switch: new environments can
     * verify payloads end-to-end without risk of accidental submission,
     * then flip to `true` once EBS wiring is confirmed correct.
     *
     * Does not gate `SalesOrderService.approveSalesOrderDraft` itself — it
     * only affects what the Oracle EBS adapter does on submit. Orgs wanting
     * to hide the Approve button entirely should use RBAC permissions
     * instead of this flag.
     */
    manualApprovalEnabled?: boolean;
    /**
     * Whether to skip description-based product matching during PO ingestion.
     * When false (default), products are matched by both identifier AND description
     * in the CustomerProduct.customerFreeform array.
     * When true, only identifier matching is used, preventing false positives when
     * multiple products share the same description (e.g., "Make-Up, 750 ml.").
     */
    skipDescriptionBasedProductMatching?: boolean;
    /**
     * Suppresses the legacy per-assignee SendGrid email fired when a user is
     * assigned to a new sales order draft (via ingest or customer reassignment).
     *
     * The workflow-based notification system (APPROVAL_NEEDED → resolve_entity_watchers
     * → send_email) is the current, more capable path for order-related notifications,
     * and this legacy path predates it. Orgs should leave this on and only flip
     * to `false` if they still depend on the legacy draft-assignment emails.
     *
     * **Defaults to `true` (legacy emails disabled) when omitted.** An org must
     * explicitly set `false` to keep receiving the legacy SendGrid assignment
     * emails. Disabling the legacy path does NOT affect the watcher assignment
     * (the CoreWatch row is still created); only the email send is skipped.
     */
    legacyDraftAssignmentEmailsDisabled?: boolean;
    /**
     * When true, multiple extracted PO records that share the same
     * purchase_order_number are reconciled into a single record before
     * downstream processing. Useful for organizations whose inbound POs
     * arrive as multi-section documents (cover page + detail page,
     * header sheet + line-item sheet) that the OCR layer sometimes
     * emits as separate records for the same underlying PO.
     *
     * Defaults to `false`. Off-by-default because the merge changes
     * the shape of extracted data (combining line items, picking the
     * richer address) and orgs should opt in explicitly once they
     * have verified the behavior matches their documents.
     */
    mergeSplitPurchaseOrdersEnabled?: boolean;
    /**
     * When true, the sales order draft table opens the EnhancedSalesOrderDraftModal
     * (richer line-item editing, EBS-aware fields, advanced workflow surfaces)
     * instead of the legacy SalesOrderDraftModal. Defaults to `false`.
     *
     * This used to be inferred from the presence of the ORACLE_EBS_INTEGRATION
     * module; it is now an explicit per-org config so the enhanced UI can be
     * adopted independently of EBS wiring.
     */
    enhancedSalesOrderUi?: boolean;
    /**
     * Org-wide fallback price list (PriceList.id) used by downstream order
     * pipelines (currently Oracle Fusion sales-order submission) when a
     * customer has no `metadata.price_list_id` override.
     *
     * When omitted, downstream code must handle the missing price list
     * explicitly; this is intentionally a soft default rather than required,
     * since not every org runs the Fusion sync.
     */
    defaultPriceListId?: string;
    /**
     * Currency of `FROZEN_ITEM_COST` stored under
     * `im_product_variants.origin_metadata`. When set to `"EUR"`, the margin
     * pipeline converts to USD using
     * `oracle-ebs-integration.config.mappings.conversionRates.EUR_USD.rate`.
     * Omitted or `"USD"` means the raw value is already in USD.
     */
    costCurrency?: "USD" | "EUR";
  };
  shipAndDebit?: {
    /** Email provider for 844 submissions */
    sd_844_email_provider?: EmailProvider;
    /** Mailbox UPN for Microsoft Graph email sending (required when provider is microsoft_graph) */
    sd_844_email_mailbox_upn?: string;
    /**
     * Org-wide JE template defaults for the GL posting Excel (Download GL
     * Posting / Post All Staged). These fill the JE Template sheet's header
     * fields. Applied to every division; `divisionName` still comes from
     * `sd_company_locations`. When a field is blank/unset, the posting builder
     * falls back to its per-division hardcoded map, then to built-in defaults.
     */
    sd_gl_je_template?: {
      /** TRANS TYPE cell (JE type code, e.g. "JE") */
      trans_type?: string;
      /** User ID cell (AS400 user, e.g. "USCHARRO") */
      user_id?: string;
      /** User email cell (semicolon-separated recipients allowed) */
      user_email?: string;
      /** Source Code cell (e.g. "SC") */
      source_code?: string;
    };
  };
  procurement?: {
    /** VoiceAgentConfig id used as the outbound caller (sender) for voice flows. */
    voiceAgentConfigId?: string;
    /** Destination number the voice flows dial (receiver), E.164. */
    phoneNumberToCall?: string;
  };
  oracleEbs?: {
    /** URL of the Oracle Query Server (e.g., http://localhost:3030) */
    queryServerUrl?: string;
    /** EBS organization name for the Order Status API (e.g., "USA", "DEMEERN") */
    ebsOrgName?: string;
    /** Default organization code when no planner mapping is found */
    defaultOrgCode?: string;
    /** Default order type when no planner mapping is found */
    defaultOrderType?: { id: number; name: string };
    /** Default price list name for price lookups (e.g., "EEME&A DISTRIBUTOR") */
    defaultPriceListName?: string;
    /** Default inventory organization ID when no planner mapping is found */
    defaultInventoryOrgId?: number;
    /**
     * Hardcoded promo code sent on every Oracle EBS sales order submission.
     * Varies by organization. Omitted from the payload when unset.
     */
    promoCode?: string;
    /**
     * Hardcoded sales channel code sent on every Oracle EBS sales order
     * submission. Varies by organization. Omitted from the payload when unset.
     */
    salesChannelCode?: string;
  };
  oracleFusion?: {
    /** Business Unit ID for sales orders (e.g., 300000002262068) */
    businessUnitId?: number;
    /** Order type code (e.g., "SG IB") */
    transactionTypeCode?: string;
    /** Fulfillment organization ID (e.g., 300000002250260) */
    fulfillmentOrganizationId?: number;
    /** Payment terms code (e.g., 4 = "30 Net") */
    paymentTermsCode?: number;
    /** Freight terms code (e.g., "DAP") */
    freightTermsCode?: string;
    /** Default currency code (e.g., "SGD") */
    defaultCurrencyCode?: string;
    /** Source transaction system identifier (e.g., "OPS") */
    sourceTransactionSystem?: string;
    /** Default line type code (e.g., "ORA_BUY") */
    defaultLineTypeCode?: string;
  };
  /**
   * Generic customer data mappings exposed via the CRUD UI.
   *
   * Each key is a map name (e.g. "plannerMap", "shippingMap").
   * Each value is a Record<entryKey, entryValue> where entryValue is an
   * arbitrary object whose shape is inferred at runtime by the UI.
   *
   * Business logic consumers should cast the entries to their expected shape.
   */
  mappings?: Record<string, Record<string, unknown>>;
  /**
   * Display metadata for each mapping in `mappings`.
   *
   * Keys correspond to the map names in `mappings`.
   * This is purely a UI concern — backend consumers ignore it.
   */
  mappingsMeta?: Record<
    string,
    {
      /** Custom display name for the mapping tab (e.g. "Planner Codes") */
      displayName?: string;
      /**
       * Label for the map-key column.
       * - A string shows the key column with that label.
       * - `null` hides the key column from the UI entirely.
       */
      keyLabel?: string | null;
      /**
       * Explicit column display order (dot-notation keys).
       * Columns not listed are appended at the end.
       * Without this, column order is unpredictable (jsonb doesn't preserve key order).
       */
      columnOrder?: string[];
    }
  >;
}

/**
 * Default `org_module_config.config` seeded for the Order Management module when
 * an organization is provisioned with it enabled (e.g. tenant-admin org creation).
 * Gives new orgs sensible OM behavior out of the box without copying the source
 * org's per-module config (which carries org-specific email-domain routing).
 */
export const DEFAULT_ORDER_MANAGEMENT_MODULE_CONFIG: ModuleConfig = {
  orderManagement: {
    autoSubmitEnabled: false,
    costCurrency: "USD",
    enabledCustomerIds: [],
    requestDateStrategy: "shipping-method",
    enhancedSalesOrderUi: true,
    manualApprovalEnabled: true,
    skipDescriptionBasedProductMatching: true,
  },
};

// Type for organization module configuration
export interface OrganizationModuleConfig {
  id: string;
  organizationId: string;
  module: ProductModule;
  enabled: boolean;
  config: ModuleConfig;
  createdAt: Date;
  updatedAt: Date;
}
